import MCButton from '@/components/ui/Buttons/MobicomButton'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { useLanguage } from '@/context/LanguageContext'
import { useTheme } from '@/context/ThemeContext'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { MNP75Unit } from '@/services/mobi-api/mnp75/types'
import { resolveImageUrl } from '@/utils/constants'
import { getSystemEnv } from '@/utils/env'
import { checkEmail, isCorpRegister, isMobile } from '@/utils/helpers'
import useIntl from '@/utils/intl'
import { Close } from '@mui/icons-material'
import { Checkbox, Dialog, TextField } from '@mui/material'
import React, { FunctionComponent } from 'react'

interface Props {
  onClick: (payload: CreateInvoiceReq) => void
  invoice: CreateInvoiceReq | undefined
  option: MNP75Unit | undefined
}

const Step2: FunctionComponent<Props> = ({ onClick, invoice, option }) => {
  const { locale } = useLanguage()
  const [email, setEmail] = React.useState<string>('')
  const [checked, setChecked] = React.useState(false)
  const [vatType, setVatType] = React.useState('CONSUMER')
  const [corpId, setCorpId] = React.useState<string>()
  const [terms, openTerms] = React.useState(false)
  const { translate } = useIntl()
  const { theme } = useTheme()

  const VAT = [
    {
      name: translate('shop.vatCons'),
      icon: '/uploads/icons/user1.svg',
      darkIcon: '/uploads/icons/user.svg',
      type: 'CONSUMER'
    },
    {
      name: translate('shop.vatCorp'),
      icon: '/uploads/icons/briefcase.svg',
      darkIcon: '/uploads/icons/briefcase1.svg',
      type: 'CORPORATE'
    }
  ]

  const handleEmail = (value: string): void => {
    setEmail(value)
  }
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChecked(event.target.checked)
  }
  const closeModal = (): void => {
    setChecked(true)
    openTerms(false)
  }
  const handleNext = (): void => {
    const payload: CreateInvoiceReq = {
      vatEmail: email,
      languageCode: locale,
      customerType: vatType === 'CORPORATE' ? 'CORPORATE' : 'CONSUMER',
      corpId: vatType === 'CORPORATE' ? corpId : undefined,
      device: isMobile(),
      redirectUrl: getSystemEnv().DOMAIN + '/paymentStatus'
    }
    onClick(payload)
  }
  const disableContinue = (): boolean => {
    let check = false
    if (!checkEmail(email ?? '')) {
      check = true
    }
    if (vatType === 'CORPORATE' && corpId !== undefined) {
      if (!isCorpRegister(corpId)) {
        check = true
      }
    }
    if (!checked) {
      check = true
    }
    if (vatType === '') {
      check = true
    }
    return check
  }
  if (invoice?.products === undefined || option === undefined) {
    return <div />
  }
  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-between overflow-auto'>
      <div className='h-[60vh] overflow-auto flex-grow'>
        <div className='md:p-8 p-4 flex flex-col gap-4 border-b border-secondaryMidStroke'>
          <span className='text-textMid text-xs md:text-sm font-bold'>{translate('order.summary')}</span>
          <div className='py-2 bg-surfaceWebGrey border border-secondaryMidStroke w-full rounded-xl'>
            <div className='flex justify-between px-4 py-2'>
              <span className='text-textMid text-xs md:text-sm font-medium'>{translate('phoneNumber')}:</span>
              <span className='text-textHigh text-xs md:text-sm font-bold'>{invoice?.isdn}</span>
            </div>
            <div className='flex justify-between px-4 py-2'>
              <span className='text-textMid text-xs md:text-sm font-medium'>{translate('data')}:</span>
              <span className='text-textHigh text-xs md:text-sm font-bold md:w-56 w-44 truncate flex justify-end'>{locale === 'en' ? option.titleEn : option.titleMn}</span>
            </div>
            <div className='flex justify-between px-4 py-2'>
              <span className='text-textMid text-xs md:text-sm font-medium'>{translate('price')}:</span>
              <span className='text-textHigh text-xs md:text-sm font-bold'>{invoice.products[0].price}₮</span>
            </div>
          </div>
        </div>
        <div className='md:p-8 p-4'>
          <span className='text-textMid text-xs md:text-sm font-bold'>{translate('shop.vat')}</span>
          <div className='flex gap-4 mt-4 w-full md:w-96'>
            {VAT.map((vat, index) => (
              <MCButton selected={vat.type === vatType} type='shop' fullwidth size='sm' onClick={() => setVatType(vat.type)} key={index} leadingIconImg={theme === 'dark' ? resolveImageUrl(vat.darkIcon) : resolveImageUrl(vat.icon)}>{vat.name}</MCButton>
            ))}
          </div>
          <div className='md:w-96 w-full'>
            <TextField
              value={email}
              id='outlined-basic'
              label={vatType === 'CORPORATE' ? translate('shop.corpEmail') : translate('shop.vatEmail')}
              variant='outlined'
              className='w-full rounded-lg md:mt-8 mt-4 text-textHigh'
              onChange={(e) => { handleEmail(e.target.value) }}
              InputLabelProps={{
                style: { color: 'var(--color-text-textHigh)' }
              }}
              InputProps={{
                style: {
                  color: 'var(--color-text-textHigh)',
                  '&::placeholder': {
                    color: 'var(--color-text-secondaryHighStroke)'
                  }
                } as any
              }}
              sx={{
                '& fieldset': {
                  borderRadius: '8px'
                }
              }}
            />
            {
            vatType === 'CORPORATE'
              ? (
                <TextField
                  value={corpId}
                  id='outlined-basic' label={translate('shop.vatCorpId')} variant='outlined' className='w-full rounded-lg md:mt-8 mt-4 text-textHigh' onChange={(e) => { setCorpId(e.target.value) }}
                  InputLabelProps={{
                    style: { color: 'var(--color-text-textHigh)' }
                  }}
                  InputProps={{
                    style: {
                      color: 'var(--color-text-textHigh)',
                      '&::placeholder': {
                        color: 'var(--color-text-secondaryHighStroke)'
                      }
                    } as any
                  }}
                  sx={{
                    '& fieldset': {
                      borderRadius: '8px'
                    }
                  }}
                />
                )
              : <></>
          }
          </div>
          <div className='flex items-center mt-10'>
            <Checkbox color='error' checked={checked} onChange={handleChange} inputProps={{ 'aria-label': 'controlled' }} className='cursor-pointer text-textHigh' />
            <p className='text-xs md:text-sm font-medium cursor-pointer hover:underline hover:underline-offset-4 hover:text-[#EF4444] text-textHigh' onClick={() => openTerms(true)}>{translate('acceptTermsOfService')}</p>
          </div>
        </div>
      </div>
      <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <DarkButton fullwidth disabled={disableContinue()} onClick={handleNext}>{translate('continue')}</DarkButton>
      </div>
      <Dialog open={terms}>
        <div className='p-4 bg-surfaceMid'>
          <div className='flex justify-between items-center w-full'>
            <span className='text-textMid font-bold text-lg'>{translate('termsOfService')}</span>
            <DarkIconButton type='text' size='sm' icon={<Close fontSize='small' />} onClick={() => openTerms(false)} />
          </div>
          <div className='py-8 px-10 border border-slate-200 rounded-lg mt-4'>
            <p className='text-lg font-bold text-textHigh'>{translate('generalProvs')}</p>
            <p className='text-sm text-textMid font-medium text-justify mt-6'>{translate('termMain')}
              <br />1. {translate('term1')}
              <br />2. {translate('term2')}
            </p>
          </div>
          <div className='mt-4 w-full flex justify-end'>
            <MCButton size='sm' onClick={closeModal}>{translate('accept')}</MCButton>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
export default Step2
